// ═══════════════════════════════════════════════
//   GAMES-REMOTE — overrides the in-memory GAME_REGISTRY / GAME_LIST with
//   the catalog stored in Supabase (tables games_registry + games_config).
//
//   Lets a designer enable/disable a minigame and tune balance numbers from
//   the Supabase dashboard without redeploying.
//
//   Strategy:
//     1. Read localStorage cache (sync, instant) → apply to in-memory arrays.
//     2. Fire network fetch in parallel; on success re-apply + refresh cache.
//     3. If Supabase fails, the data.js defaults remain in place.
//
//   Notes:
//     - games_registry.enabled=false removes the row from GAME_REGISTRY +
//       GAME_LIST (and therefore from the games window and leaderboard tabs).
//     - games_config.config jsonb is shallow-merged into the matching
//       row.tunables. Designers can tweak just one knob without resending the
//       whole object.
//     - Unknown keys in DB are ignored (no game module to launch them).
//     - The remote catalog is cached per-browser, not per-user.
// ═══════════════════════════════════════════════

const CACHE_KEY = 'konbini-games-cache-v1';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1h

function _readCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.ts || Date.now() - parsed.ts > CACHE_TTL_MS) return null;
        return parsed.data;
    } catch (e) { return null; }
}

function _writeCache(data) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data })); }
    catch (e) { /* quota or disabled — ignore */ }
}

// Snapshot of the original data.js defaults so we can fall back / re-apply
// without losing fields the DB row doesn't override.
let _defaults = null;
function _captureDefaults() {
    if (_defaults) return;
    _defaults = {
        registry: window.GAME_REGISTRY.map(g => ({ ...g, tunables: g.tunables ? { ...g.tunables } : g.tunables })),
    };
}

function _rebuildGameList() {
    if (!Array.isArray(window.GAME_LIST)) return;
    window.GAME_LIST.length = 0;
    for (const g of window.GAME_REGISTRY) {
        window.GAME_LIST.push({
            key: g.key, name: g.name, desc: g.desc, pts: g.pts,
            icon: g.icon, cat: g.cat, link: g.link,
        });
    }
}

function _apply({ registry, configs }) {
    _captureDefaults();
    if (!registry || !registry.length) return false;

    // Build a lookup of default rows by key (frozen template).
    const defaultByKey = new Map(_defaults.registry.map(g => [g.key, g]));

    // Filter + sort according to remote registry (enabled rows only).
    const merged = [];
    for (const row of registry) {
        if (row.enabled === false) continue;
        const base = defaultByKey.get(row.key);
        if (!base) continue; // unknown key in DB — no game module wired
        const merged_row = { ...base };
        if (row.name)        merged_row.name = row.name;
        if (row.icon)        merged_row.icon = row.icon;
        if (row.cat)         merged_row.cat  = row.cat;
        if (row.link_win)    merged_row.linkWin = row.link_win;
        if (row.link_opener) merged_row.linkOpener = row.link_opener;
        if (typeof row.link === 'boolean') merged_row.link = row.link;
        // Fresh tunables clone so we can mutate without affecting defaults.
        if (base.tunables) merged_row.tunables = { ...base.tunables };
        merged.push(merged_row);
    }

    // Apply per-game config overrides (shallow merge into tunables).
    if (configs) {
        const mergedByKey = new Map(merged.map(g => [g.key, g]));
        for (const c of configs) {
            const target = mergedByKey.get(c.key);
            if (!target || !target.tunables || !c.config) continue;
            Object.assign(target.tunables, c.config);
        }
    }

    // Mutate GAME_REGISTRY in place (other modules hold the array reference).
    window.GAME_REGISTRY.length = 0;
    window.GAME_REGISTRY.push(...merged);
    _rebuildGameList();
    return true;
}

async function loadGames() {
    _captureDefaults();
    const cached = _readCache();
    if (cached) _apply(cached);
    if (!window.DB || !window.DB.available()) return;
    try {
        const res = await window.DB.loadGames();
        if (res && res.registry) {
            _apply(res);
            _writeCache(res);
        } else if (res && res.error) {
            console.warn('[Konbini games] remote load error', res.error.message || res.error);
        }
    } catch (e) {
        console.warn('[Konbini games] remote load threw', e);
    }
}

// Kick off as soon as this module loads. Sync part runs immediately; network
// resolves in the background and re-applies when ready.
loadGames();

window.loadGames = loadGames;
